import { Spinner } from '@cimplico/components';
import { createContext, ReactNode, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { useUserWorkpaperRole } from '../queries/use-user-workpaper-role';
import { useWorkpaper } from '../queries/use-workpaper';
import { useWorkpaperCompletionStatus } from '../queries/use-workpaper-completion-status';
import { Workpaper, WorkpaperCompleteStatusEnum, WorkpaperRoleEnum, WorkpaperStatusEnum } from '../types/workpaper';
import { useFirmContext } from './FirmProvider';
import { useUserContext } from './UserProvider';
import { SubscriptionStatus } from '../types/firm';
import { UserRoleEnum } from '../types/user';
import { useUserFirmRoleContext } from './UserFirmRoleProvider';
import { Entity } from '../types/entity';
import { useEntity } from '../queries/use-entity';
import ErrorSplashScreen from '../pages/ErrorSplashScreen';

type WorkpaperContextType = Omit<Workpaper, 'entity' | 'id'> & {
    id: string;
    role: WorkpaperRoleEnum;
    readOnly: boolean;
    completeOutcome?: {
        completeError: { message: string };
        createdAt: Date;
        status: WorkpaperCompleteStatusEnum;
        updatedAt: Date;
        workpaperId: string;
    };
    entity: Entity;
};

const WorkpaperContext = createContext<WorkpaperContextType | undefined>(undefined);

type WorkpaperProviderProps = {
    children: ReactNode | ReactNode[];
};

function WorkpaperProvider({ children }: WorkpaperProviderProps): JSX.Element {
    const { workpaperId } = useParams();
    const { id: userId } = useUserContext();
    const { shortId, subscription } = useFirmContext();
    const role = useUserFirmRoleContext();
    const workpaperQuery = useWorkpaper(workpaperId);
    const entityQuery = useEntity(workpaperQuery.data?.workpaper.entity.id);

    // Get completing status
    const completionStatusQuery = useWorkpaperCompletionStatus(
        workpaperId,
        workpaperQuery.data?.workpaper?.status === WorkpaperStatusEnum.ACTIVE
    );

    // Get the role
    const userWorkpaperRoleQuery = useUserWorkpaperRole({ userId, workpaperId });
    if (userWorkpaperRoleQuery.isLoading || workpaperQuery.isLoading || completionStatusQuery.isLoading) {
        return (
            <div className="absolute w-full h-[var(--remaining-screen-height)]">
                <div className="absolute top-1/3 left-1/2">
                    <Spinner />
                </div>
            </div>
        );
    }

    if (!userWorkpaperRoleQuery.isSuccess || userWorkpaperRoleQuery.isError) {
        if (userWorkpaperRoleQuery.error?.response?.status === 404) {
            return (
                <ErrorSplashScreen
                    errorCode={404}
                    title="Workpaper not found."
                    redirectTitle="Back to workpapers"
                    redirectUrl={`/${shortId}/workpapers`}
                    leftArrow={true}
                />
            );
        }

        return (
            <ErrorSplashScreen
                title="An error has occurred."
                redirectTitle="Back to workpapers"
                redirectUrl={`/${shortId}/workpapers`}
                leftArrow={true}
            />
        );
    }

    if (workpaperQuery.isError || !workpaperQuery.isSuccess || !workpaperId) {
        if (workpaperQuery.error?.response?.status === 404) {
            return (
                <ErrorSplashScreen
                    errorCode={404}
                    title="Workpaper not found."
                    redirectTitle="Back to workpapers"
                    redirectUrl={`/${shortId}/workpapers`}
                    leftArrow={true}
                />
            );
        }
        if (workpaperQuery.error?.response?.status === 403) {
            return (
                <ErrorSplashScreen
                    errorCode={403}
                    title="Permission denied."
                    redirectTitle="Back to workpapers"
                    redirectUrl={`/${shortId}/workpapers`}
                    leftArrow={true}
                />
            );
        }
        return (
            <ErrorSplashScreen
                title="An error has occurred."
                redirectTitle="Back to workpapers"
                redirectUrl={`/${shortId}/workpapers`}
                leftArrow={true}
            />
        );
    }

    if (!workpaperQuery.data?.workpaper || !entityQuery.data?.entity) {
        return <></>;
    }

    const value = {
        ...workpaperQuery.data.workpaper,
        groups: workpaperQuery.data?.workpaper?.groups ?? [],
        role: userWorkpaperRoleQuery.data?.role,
        completeOutcome: completionStatusQuery.data?.status,
        entity: entityQuery.data?.entity,
        readOnly:
            (subscription?.status !== SubscriptionStatus.ACTIVE && subscription?.status !== SubscriptionStatus.TRIALING) ||
            workpaperQuery.data?.workpaper?.status !== WorkpaperStatusEnum.ACTIVE ||
            completionStatusQuery.data?.status?.status === WorkpaperCompleteStatusEnum.COMPLETING ||
            userWorkpaperRoleQuery.data?.role === WorkpaperRoleEnum.GUEST,
    };

    return <WorkpaperContext.Provider value={value}>{children}</WorkpaperContext.Provider>;
}

function useWorkpaperContext(): WorkpaperContextType {
    const context = useContext(WorkpaperContext);
    if (context === undefined) {
        throw new Error('useWorkpaperContext must be used within a WorkpaperProvider');
    }
    return context;
}

export { WorkpaperProvider, useWorkpaperContext };
